import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import claStep1Image from "@/assets/cla-step-1-mystery-shopping.png";
import claStep2Image from "@/assets/cla-step-2-competitor-crawling.png";
import claStep3Image from "@/assets/cla-step-3-volumes-difficulty.png";
import claStep4Image from "@/assets/cla-step-4-serp-analysis.png";
import claStep5Image from "@/assets/cla-step-5-ai-overview.png";
import claStep6Image from "@/assets/cla-step-6-llm-visibility.png";
// Report step images
import claReportStep1Image from "@/assets/cla-report-step-1-preparing-data.png";
import claReportStep2Image from "@/assets/cla-report-step-2-generating-sections.png";
import claReportStep3Image from "@/assets/cla-report-step-3-finalizing-report.png";

export interface ProgressStep {
  name: string;
  label: string;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'skipped';
  description?: string;
  image: string;
}

interface CLAProgressState {
  isOpen: boolean;
  mode: 'analysis' | 'report' | null;
  projectId: string | null;
  projectName: string;
  steps: ProgressStep[];
  currentStepIndex: number;
  overallStatus: 'pending' | 'running' | 'completed' | 'failed';
  statusMessage: string;
}

interface CLAProgressContextValue extends CLAProgressState {
  openAnalysisProgress: (projectId: string, projectName: string) => void;
  openReportProgress: (projectId: string, projectName: string) => void;
  closeProgress: () => void;
  updateStepStatus: (stepName: string, status: ProgressStep['status'], description?: string) => void;
  setCurrentStepIndex: (index: number) => void;
}

const CLAProgressContext = createContext<CLAProgressContextValue | undefined>(undefined);

// Analysis steps configuration with descriptions
const ANALYSIS_STEPS: Omit<ProgressStep, 'status'>[] = [
  { 
    name: 'collect_seo_analysis', 
    label: 'SEO Analysis', 
    image: claStep1Image,
    description: 'Analyzing competitor websites for organic rankings, domain authority, and technical SEO metrics.'
  },
  { 
    name: 'collect_content_crawl', 
    label: 'Content Crawling', 
    image: claStep2Image,
    description: 'Extracting and analyzing content from competitor pages to understand their messaging and positioning.'
  },
  { 
    name: 'collect_keyword_enrichment', 
    label: 'Keyword Enrichment', 
    image: claStep3Image,
    description: 'Gathering search volume, difficulty scores, and related keywords from your target market.'
  },
  { 
    name: 'collect_serp_analysis', 
    label: 'SERP Analysis', 
    image: claStep4Image,
    description: 'Analyzing search engine results pages to identify top-ranking content and SERP features.'
  },
  { 
    name: 'collect_ai_overview', 
    label: 'AI Overview', 
    image: claStep5Image,
    description: 'Capturing AI-generated answers and featured snippets that appear for your keywords.'
  },
  { 
    name: 'analyze_llms', 
    label: 'LLM Analysis', 
    image: claStep6Image,
    description: 'Using AI to synthesize findings and generate strategic insights from collected data.'
  },
];

// Report steps configuration with dedicated images
const REPORT_STEPS: Omit<ProgressStep, 'status'>[] = [
  { 
    name: 'prepare_data', 
    label: 'Preparing Data', 
    image: claReportStep1Image,
    description: 'Organizing and structuring all collected analysis data for report generation.'
  },
  { 
    name: 'generate_sections', 
    label: 'Generating Sections', 
    image: claReportStep2Image,
    description: 'Creating detailed report sections with insights, charts, and recommendations.'
  },
  { 
    name: 'finalize_report', 
    label: 'Finalizing Report', 
    image: claReportStep3Image,
    description: 'Compiling all sections into a cohesive final report with executive summary.'
  },
];

const initialState: CLAProgressState = {
  isOpen: false,
  mode: null,
  projectId: null,
  projectName: '',
  steps: [],
  currentStepIndex: 0,
  overallStatus: 'pending',
  statusMessage: 'Getting ready...',
};

export function CLAProgressProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<CLAProgressState>(initialState);
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const initializeSteps = (stepConfigs: typeof ANALYSIS_STEPS): ProgressStep[] => {
    return stepConfigs.map(step => ({
      ...step,
      status: 'pending' as const,
      description: 'Pending',
    }));
  };

  const openAnalysisProgress = useCallback((projectId: string, projectName: string) => {
    setState({
      isOpen: true,
      mode: 'analysis',
      projectId,
      projectName,
      steps: initializeSteps(ANALYSIS_STEPS),
      currentStepIndex: 0,
      overallStatus: 'running',
      statusMessage: 'Starting analysis...',
    });
  }, []);

  const openReportProgress = useCallback((projectId: string, projectName: string) => {
    setState({
      isOpen: true,
      mode: 'report',
      projectId,
      projectName,
      steps: initializeSteps(REPORT_STEPS),
      currentStepIndex: 0,
      overallStatus: 'running',
      statusMessage: 'Generating report...',
    });
  }, []);

  const closeProgress = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    setState(initialState);
  }, []);

  const updateStepStatus = useCallback((stepName: string, status: ProgressStep['status'], description?: string) => {
    setState(prev => {
      const newSteps = prev.steps.map(step => {
        if (step.name === stepName) {
          return { ...step, status, description: description || step.description };
        }
        return step;
      });

      // Find current running step index - prioritize running step, then stay on completed step
      const runningIndex = newSteps.findIndex(s => s.status === 'running');
      let newCurrentIndex = prev.currentStepIndex;

      if (runningIndex >= 0) {
        // If there's a running step, always scroll to it
        newCurrentIndex = runningIndex;
      } else if (status === 'completed') {
        // If we just completed a step, stay on it momentarily
        const justCompletedIndex = newSteps.findIndex(s => s.name === stepName);
        if (justCompletedIndex >= 0) {
          newCurrentIndex = justCompletedIndex;
        }
      }

      // Determine overall status
      const hasRunning = newSteps.some(s => s.status === 'running');
      const hasFailed = newSteps.some(s => s.status === 'failed');
      const allCompleted = newSteps.every(s => s.status === 'completed' || s.status === 'skipped');

      let overallStatus: CLAProgressState['overallStatus'] = prev.overallStatus;
      let statusMessage = prev.statusMessage;

      if (hasRunning) {
        overallStatus = 'running';
        const runningStep = newSteps.find(s => s.status === 'running');
        statusMessage = runningStep ? `${runningStep.label}...` : 'Processing...';
      } else if (hasFailed) {
        overallStatus = 'failed';
        statusMessage = 'Analysis failed';
      } else if (allCompleted) {
        overallStatus = 'completed';
        statusMessage = prev.mode === 'analysis' ? 'Analysis complete!' : 'Report generated!';
      }

      return {
        ...prev,
        steps: newSteps,
        currentStepIndex: newCurrentIndex,
        overallStatus,
        statusMessage,
      };
    });
  }, []);

  const setCurrentStepIndex = useCallback((index: number) => {
    setState(prev => ({ ...prev, currentStepIndex: index }));
  }, []);

  // Polling for execution logs
  useEffect(() => {
    if (!state.isOpen || !state.projectId || state.mode !== 'analysis') {
      return;
    }

    const fetchExecutionLogs = async () => {
      try {
        // Get the most recent execution for this project
        const { data: latestLog, error: latestLogError } = await supabase
          .from('os_cla_execution_logs')
          .select('execution_id')
          .eq('project_id', state.projectId!)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (latestLogError) {
          console.error('Error fetching latest execution log:', latestLogError);
          return;
        }

        if (!latestLog?.execution_id) return;

        // Fetch all logs for this execution
        const { data: logs, error } = await supabase
          .from('os_cla_execution_logs')
          .select('*')
          .eq('project_id', state.projectId!)
          .eq('execution_id', latestLog.execution_id)
          .order('created_at', { ascending: true });

        if (error) {
          console.error('Error fetching CLA execution logs:', error);
          return;
        }

        // Update step statuses based on logs
        if (logs && logs.length > 0) {
          logs.forEach((log: any) => {
            const stepName = log.step_name;
            if (stepName === 'collect_data' || stepName === 'generate_report') return;

            let description = log.status === 'running' ? 'Processing...' : 
                             log.status === 'completed' ? 'Completed' :
                             log.status === 'failed' ? log.error_message || 'Failed' :
                             'Pending';

            // Extract result info for completed steps
            if (log.status === 'completed' && log.result) {
              const result = log.result;
              if (result.results_count !== undefined) {
                description = `${result.results_count} results`;
              } else if (result.keywords_enriched !== undefined) {
                description = `${result.keywords_enriched} keywords enriched`;
              }
            }

            updateStepStatus(stepName, log.status, description);
          });
        }
      } catch (error) {
        console.error('Error in fetchExecutionLogs:', error);
      }
    };

    fetchExecutionLogs();
    pollIntervalRef.current = setInterval(fetchExecutionLogs, 2000);

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [state.isOpen, state.projectId, state.mode, updateStepStatus]);

  // Stop polling when completed or failed
  useEffect(() => {
    if (state.overallStatus === 'completed' || state.overallStatus === 'failed') {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    }
  }, [state.overallStatus]);

  const value: CLAProgressContextValue = {
    ...state,
    openAnalysisProgress,
    openReportProgress,
    closeProgress,
    updateStepStatus,
    setCurrentStepIndex,
  };

  return (
    <CLAProgressContext.Provider value={value}>
      {children}
    </CLAProgressContext.Provider>
  );
}

export function useCLAProgress() {
  const context = useContext(CLAProgressContext);
  if (context === undefined) {
    throw new Error('useCLAProgress must be used within a CLAProgressProvider');
  }
  return context;
}
